import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Alert, Spinner } from 'react-bootstrap';
import { FaClock, FaClipboardCheck, FaMapMarkedAlt, FaSignInAlt, FaSignOutAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { sitesAPI, checkinsAPI, statisticsAPI, reportsAPI } from '../../utils/api';

const PersonnelDashboard = () => {
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [recentSites, setRecentSites] = useState([]);
  const [stats, setStats] = useState({
    totalHours: 0,
    sitesVisited: 0,
    reportsSubmitted: 0
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCheckInStatus = async () => {
      try {
        // Get active check-in status from the API
        const response = await checkinsAPI.getActive();
        
        if (response.data?.active && response.data.checkIn?.id) {
          const checkInData = response.data.checkIn;
          setIsCheckedIn(true);
          setCurrentSession({
            checkInTime: checkInData.checkInTime,
            siteName: checkInData.site?.name || 'Unknown Site'
          });
        } else {
          setIsCheckedIn(false);
          setCurrentSession(null);
        }
      } catch (error) {
        console.error("Failed to fetch check-in status:", error);
        setIsCheckedIn(false);
        setCurrentSession(null);
      }
    };

    const fetchRecentSites = async () => {
      try {
        const sitesResponse = await sitesAPI.getAssigned();
        const checkInsResponse = await checkinsAPI.getHistory({ limit: 20 });

        const siteLastVisited = {};

        if (checkInsResponse.data && checkInsResponse.data.data && checkInsResponse.data.data.length > 0) {
          checkInsResponse.data.data.forEach(checkIn => {
            if (checkIn.siteId && !siteLastVisited[checkIn.siteId]) {
              siteLastVisited[checkIn.siteId] = checkIn.checkInTime;
            }
          });
        }

        const formattedSites = sitesResponse.data.map(site => ({
          id: site.id,
          name: site.name,
          address: site.address,
          lastVisited: siteLastVisited[site.id]
            ? new Date(siteLastVisited[site.id]).toLocaleDateString()
            : 'Not visited yet'
        }));

        setRecentSites(formattedSites);
      } catch (error) {
        console.error("Failed to fetch assigned sites:", error);
      }
    };

    const fetchStats = async () => {
      try {
        // Get check-in history to calculate stats directly
        const checkInsResponse = await checkinsAPI.getHistory({ timeRange: 'month' });
        const reportsResponse = await reportsAPI.getUserReports({ timeRange: 'month' });
        
        // Calculate total hours from check-ins
        let totalHoursValue = 0;
        const visitedSiteIds = new Set();
        
        if (checkInsResponse && checkInsResponse.data && checkInsResponse.data.length > 0) {
          checkInsResponse.data.forEach(checkIn => {
            // Add site to visited sites if it has a valid ID
            if (checkIn.siteId) {
              visitedSiteIds.add(checkIn.siteId);
            }
            
            // Calculate duration if both check-in and check-out times exist
            if (checkIn.checkInTime && checkIn.checkOutTime) {
              const checkInTime = new Date(checkIn.checkInTime);
              const checkOutTime = new Date(checkIn.checkOutTime);
              const durationHours = (checkOutTime - checkInTime) / (1000 * 60 * 60); // in hours
              totalHoursValue += durationHours;
            }
          });
        }
        
        // Count reports
        const reportsCount = reportsResponse?.data?.length || 0;
        
        // Try to get stats from API first, fall back to calculated values
        const response = await statisticsAPI.getUserStats('month');
        
        if (response && response.data) {
         setStats({
          totalHours: Number(response.data.stats.totalHours ?? totalHoursValue).toFixed(1),
          sitesVisited: new Set(response.data.checkIns.map(ci => ci.site?.id)).size,
          reportsSubmitted: response.data.reports.length
        });

        } else {
          // Use calculated values if no API data is returned
          setStats({
          totalHours: Number(totalHoursValue).toFixed(1),
          sitesVisited: visitedSiteIds.size,
          reportsSubmitted: reportsCount
        });

        }
      } catch (error) {
        console.error("Failed to fetch stats:", error);
        // Set default values if API call fails
        setStats({
          totalHours: 0,
          sitesVisited: 0,
          reportsSubmitted: 0
        });
        // Don't show error in UI for stats, just silently use defaults
      }
    };

    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchCheckInStatus(),
          fetchRecentSites(),
          fetchStats()
        ]);
        setError('');
      } catch (err) {
        setError('Failed to load dashboard data. Please try again later.');
        console.error('Dashboard data loading error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <Container fluid className="py-3 py-md-4">
      <Row className="mb-3 mb-md-4">
        <Col>
          <h1 className="mb-2 mb-md-4">Personnel Dashboard</h1>
        </Col>
      </Row>
      
      {error && (
        <Alert variant="danger" className="mb-3 mb-md-4">
          {error}
        </Alert>
      )}
      
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" className="mb-3">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading dashboard data...</p>
        </div>
      ) : (
        <>
          {/* Status Card */}
          <Card className="mb-3 mb-md-4 dashboard-card">
            <Card.Body>
              <Card.Title className="mb-3">Current Status</Card.Title>
              <div className="d-flex align-items-center mb-3">
                <div className={`status-indicator ${isCheckedIn ? 'bg-success' : 'bg-secondary'} me-3`} 
                     style={{ width: '20px', height: '20px', borderRadius: '50%' }}></div>
                <div>
                  {isCheckedIn ? (
                    <>
                      <h5 className="mb-1">Currently Checked In</h5>
                      {currentSession && (
                        <p className="text-muted mb-0 small">
                          Since: {new Date(currentSession.checkInTime).toLocaleTimeString()} at {currentSession.siteName}
                        </p>
                      )}
                    </>
                  ) : (
                    <h5 className="mb-0">Currently Checked Out</h5>
                  )}
                </div>
              </div>
              <div className="d-flex justify-content-center mt-3">
                <Link to="/personnel/check" className={`btn ${isCheckedIn ? 'btn-danger' : 'btn-primary'} w-100 w-md-auto d-flex align-items-center justify-content-center gap-2`}>
                  {isCheckedIn ? (
                    <>
                      <FaSignOutAlt />
                      <span>Check Out</span>
                    </>
                  ) : (
                    <>
                      <FaSignInAlt />
                      <span>Check In</span>
                    </>
                  )}
                </Link>
              </div>
            </Card.Body>
          </Card>
          
          {/* Stats Cards */}
          <Row className="mb-3 mb-md-4 g-3 g-md-4 align-items-stretch">
            <Col xs={12} sm={6} lg={4} className="mb-3 d-flex">
              <Card className="h-100 dashboard-card w-100">
                <Card.Body className="d-flex align-items-center">
                  <div className="rounded-circle bg-primary bg-opacity-10 p-3 me-3" style={{ width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FaClock className="text-primary" size={20} />
                  </div>
                  <div>
                    <Card.Title className="small mb-1">Total Hours</Card.Title>
                    <div className="d-flex align-items-baseline gap-2">
                      <h3 className="mb-0 h4">{stats.totalHours}</h3>
                      <span className="text-muted small">This month</span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} sm={6} lg={4} className="mb-3 d-flex">
              <Card className="h-100 dashboard-card w-100">
                <Card.Body className="d-flex align-items-center">
                  <div className="rounded-circle bg-success bg-opacity-10 p-3 me-3" style={{ width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FaMapMarkedAlt className="text-success" size={20} />
                  </div>
                  <div>
                    <Card.Title className="small mb-1">Sites Visited</Card.Title>
                    <div className="d-flex align-items-baseline gap-2">
                      <h3 className="mb-0 h4">{stats.sitesVisited}</h3>
                      <span className="text-muted small">This month</span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} sm={6} lg={4} className="mb-3 d-flex">
              <Card className="h-100 dashboard-card w-100">
                <Card.Body className="d-flex align-items-center">
                  <div className="rounded-circle bg-info bg-opacity-10 p-3 me-3" style={{ width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FaClipboardCheck className="text-info" size={20} />
                  </div>
                  <div>
                    <Card.Title className="small mb-1">Reports Submitted</Card.Title>
                    <div className="d-flex align-items-baseline gap-2">
                      <h3 className="mb-0 h4">{stats.reportsSubmitted}</h3>
                      <span className="text-muted small">This month</span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          
          {/* Recent Sites */}
          <Card className="dashboard-card">
            <Card.Body>
              <Card.Title className="mb-3">Your Assigned Sites</Card.Title>
              {recentSites.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Site Name</th>
                        <th className="d-none d-md-table-cell">Address</th>
                        <th className="d-none d-sm-table-cell">Last Visited</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentSites.map(site => (
                        <tr key={site.id}>
                          <td>
                            <div>
                              <div className="fw-bold">{site.name}</div>
                              <small className="text-muted d-md-none">{site.address}</small>
                            </div>
                          </td>
                          <td className="d-none d-md-table-cell">{site.address}</td>
                          <td className="d-none d-sm-table-cell">{site.lastVisited}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <Alert variant="info">
                  You don't have any sites assigned to you yet. Please contact your administrator.
                </Alert>
              )}
              <div className="d-flex justify-content-center mt-4">
                <Link to="/personnel/check" className="btn btn-outline-primary w-100 w-md-auto d-flex align-items-center justify-content-center gap-2">
                  <FaSignInAlt />
                  <span>Check In/Out</span>
                </Link>
              </div>
            </Card.Body>
          </Card>
        </>
      )}
    </Container>
  );
};

export default PersonnelDashboard;
